import { useState, useEffect, useCallback } from 'react';
import { Table, TableHeader, TableBody, Column, Row } from 'react-aria-components';
import { useLocation } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getOffices } from '@/api/getOffices';
import { getVehicles } from '@/api/getVehicles';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { CreateOrEditVehicleDialog } from '@/components/vehicles/CreateOrEditVehicleDialog';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { Vehicle } from '@/types/vehicle';

export const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | undefined>(undefined);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [isEditOpenDialog, setIsEditOpenDialog] = useState(false);

  const [loginUser] = useAtom(UserAtom);
  const [_, setNotification] = useAtom(NotificationAtom);

  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '車両マスタ' }];

  const fetchVehicles = useCallback(async () => {
    try {
      const result = await getVehicles(loginUser?.email);
      if (result.status === 200) {
        setVehicles(result.result);
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '車両マスタの取得に失敗しました',
        message: '車両マスタの取得に失敗しました。',
      });
    }
  }, [loginUser?.email]);

  const fetchOffices = useCallback(async () => {
    try {
      const result = await getOffices(loginUser?.email || '');
      if (result.status === 200) {
        setOffices(result.result);
      }
    } catch (error) {
      console.log('error');
    }
  }, [loginUser?.email]);

  useEffect(() => {
    fetchVehicles();
    fetchOffices();
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-4 mt-5 flex flex-col items-start justify-between gap-5">
        <h1 className="text-2xl font-bold">車両マスタ</h1>
        <div className="flex-shrink-0">
          <Button
            type="submit"
            size="lg"
            variant="solid-fill"
            onClick={() => setIsOpenCreateDialog(true)}
          >
            新規登録
          </Button>
        </div>
      </div>
      {isOpenCreateDialog && (
        <CreateOrEditVehicleDialog
          isOpen={isOpenCreateDialog}
          setIsOpen={setIsOpenCreateDialog}
          afterSubmit={() => {
            fetchVehicles();
          }}
          offices={offices}
        />
      )}

      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            No
          </Column>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            車種
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            車両タイプ
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            乗車可能人数（※運転手含めない）
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            ナンバープレート
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">編集</Column>
        </TableHeader>
        <TableBody>
          {vehicles.map((vehicle, index) => (
            <Row key={index}>
              <Column
                key="no"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {vehicle.id}
              </Column>
              <Column
                key="name"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {vehicle.name}
              </Column>
              <Column
                key="type"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {vehicle.type}
              </Column>
              <Column
                key="capacity"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {`${vehicle.capacity}人`}
              </Column>
              <Column
                key="number"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {vehicle.number}
              </Column>
              <Column
                key="edit"
                className="border border-gray-200 px-4 py-2"
              >
                <Button
                  size="sm"
                  variant="text"
                  type="submit"
                  onClick={() => {
                    setIsEditOpenDialog(true);
                    setSelectedVehicle(vehicle);
                  }}
                >
                  編集
                </Button>
              </Column>
            </Row>
          ))}
        </TableBody>
      </Table>
      {isEditOpenDialog && (
        <CreateOrEditVehicleDialog
          isOpen={isEditOpenDialog}
          setIsOpen={setIsEditOpenDialog}
          afterSubmit={() => {
            fetchVehicles();
          }}
          vehicle={selectedVehicle}
          offices={offices}
        />
      )}
    </div>
  );
};
